import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import './css/style.css';
import './mosaic/charts/ChartjsConfig';
import {GlobalContext} from "./utils/context/GlobalContextProvider.jsx";
import Routing from "./Routing.jsx";
import i18next from "i18next";
import {initReactI18next} from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import translations_cs_CZ from "../translations/translations.cs-CZ.json";
import {SystemApi, UserApi} from "./api/authority/generated/index.ts";
import BigLoader from "./components/loader/BigLoader.jsx";
import {getProjectIdFromPath} from "./utils/projectRouting.js";
import {
    clearAddonFlowDismissed,
    getProjectOnboardingRedirect,
    getZeroProjectOnboardingRedirect,
    isAddonFlowDismissed,
} from "./utils/onboardingRouting.js";
import {isSessionAuthenticationError} from './api/utilities/sessionAuthentication.js';
import {shouldKeepInvitationRoute} from './pages/auth/ContinueFromInvitation.jsx';
import {
    getLocalStoragePendingAddon,
    setLocalStoragePendingAddon,
    setLocalStorageUpgatesPendingToken
} from "./utils/localStorage.js";
import {isSignOutPath, shouldLoadBootstrapData} from "./utils/signOutRouting.js";
import {getAnalyticsPath} from "./pages/auth/mcpConsent.js";

function DatixoApplication({onFatalError}) {
    const {
        context,
        setCurrentProjectId,
        setLocalizationLoaded,
        setCurrentIdentity,
        addCurrentUserProject,
        setCurrentUserProjectsLoaded,
        setCurrentUserProjectsReload,
        getAuthorityConfiguration,
        setDefaultSubscriptionValues,
        setDefaultSubscriptionValuesLoaded,
        setSupportedCurrencies,
        setSupportedCurrenciesLoaded,
        setPermissions,
    } = useContext(GlobalContext);

    const authorityConfiguration = getAuthorityConfiguration();
    const navigate = useNavigate();
    const location = useLocation();
    const loadBootstrapData = shouldLoadBootstrapData(location.pathname);
    const [searchParams, setSearchParams] = useSearchParams();
    const routeProjectId = getProjectIdFromPath(location.pathname);
    const routeProjectExists = routeProjectId === null || context.currentUserProjects.some(
        (project) => project.id === routeProjectId
    );

    useEffect(() => {
        if (!loadBootstrapData) return;
        if (
            !context.currentUserProjectsLoaded ||
            routeProjectId === null ||
            !routeProjectExists ||
            routeProjectId === context.currentProjectId
        ) {
            return;
        }

        setCurrentProjectId(routeProjectId);
    }, [
        context.currentProjectId,
        context.currentUserProjectsLoaded,
        routeProjectExists,
        routeProjectId,
        loadBootstrapData,
        setCurrentProjectId,
    ]);

    useEffect(() => {
        if (searchParams.get('reset_token') !== '1') {
            return;
        }

        setLocalStoragePendingAddon(null);
        setLocalStorageUpgatesPendingToken(null);

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('reset_token');
        setSearchParams(nextSearchParams, {replace: true});
        navigate('/', {replace: true});
    }, [navigate, searchParams, setSearchParams]);

    useEffect(() => {
        const pendingAddon = getAddonFromSearch(searchParams, location.search);

        if (pendingAddon === null) {
            return;
        }

        setLocalStoragePendingAddon(pendingAddon);
        if (pendingAddon.type === 'upgates') {
            setLocalStorageUpgatesPendingToken(pendingAddon.token);
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('addon_type');
        nextSearchParams.delete('addon_token');
        nextSearchParams.delete('upgates_token');
        nextSearchParams.delete('lang');
        for (const key of [...nextSearchParams.keys()]) {
            if (key.startsWith('upgates_token')) {
                nextSearchParams.delete(key);
            }
        }
        setSearchParams(nextSearchParams, {replace: true});
    }, [location.search, searchParams, setSearchParams]);

    useEffect(() => {
        const hasPendingAddon = getLocalStoragePendingAddon() !== null;

        if (context.currentIdentity || !hasPendingAddon) {
            return;
        }

        if (
            isSignOutPath(location.pathname) ||
            [
                '/sign/in/addon',
                '/reset-password',
                '/sign/up/continue-with-email',
                '/sign/up/email',
                '/invite',
            ].includes(location.pathname)
        ) {
            return;
        }

        navigate('/sign/in/addon', {replace: true});
    }, [context.currentIdentity, location.pathname, navigate]);

    // DataLayer - user and project information
    useEffect(() => {
        const user = context.currentIdentity?.user;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'pageView',
            project: context.currentProjectName,
            userId: user?.id,
            userName: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined,
            userMail: user?.email,
            path: getAnalyticsPath(location.pathname, location.search),
        });
    }, [location.pathname, location.search, context.currentProjectName, context.currentIdentity]);

    // Facebook SDK
    useEffect(() => {
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: import.meta.env.VITE_META_APP_ID,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v19.0'
            });
        };

        (function (d, s, id) {
            let js, fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;
            js = d.createElement(s);
            js.id = id;
            js.src = "https://connect.facebook.net/en_US/sdk.js";
            fjs.parentNode.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));
    }, []);

    // Load translations
    useEffect(() => {
        i18next
            .use(initReactI18next)
            .use(LanguageDetector)
            .use(resourcesToBackend(locateTranslation))
            .init({
                ns: ['translations'],
                lngs: ['cs-CZ'],
                defaultNs: 'translations',
                defaultLng: 'cs-CZ',
                fallbackLng: 'cs-CZ',
            })
            .then(() => setLocalizationLoaded(true));
    }, []);

    // Check user identity and log out on invalid token
    useEffect(() => {
        if (!loadBootstrapData) return;
        if (
            Object.keys(authorityConfiguration.headers).length === 0 ||
            !context.currentIdentity
        ) {
            return;
        }

        new UserApi(authorityConfiguration)
            .userDetail({userId: context.currentIdentity?.user.id})
            .catch(() => {
                // Confirmed authenticated 401 responses are handled by the shared session middleware.
                // Network and provider failures must not destroy a still-valid local session.
            });
    }, [context.currentIdentity, loadBootstrapData]);

    // Load user projects only once if logged in
    useEffect(() => {
        const tokenForProject = context.currentIdentity?.token ?? null;
        let active = true;

        if (!loadBootstrapData) return;

        if (!context.currentIdentity?.user.id) {
            setCurrentUserProjectsLoaded(true, tokenForProject); // No user, mark as loaded
            return;
        }

        let userId = context.currentIdentity.user.id;

        async function fetchProjects() {
            //let allProjects = [];

            const api = new UserApi(authorityConfiguration);

            let page = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const response = await api.userProjects({userId, page});
                response.data.forEach(project => addCurrentUserProject(project, tokenForProject));
                hasNextPage = response.pagination.hasNextPage;
                page += 1;
            }

            setCurrentUserProjectsLoaded(true, tokenForProject);


        }

        fetchProjects().catch((error) => {
            if (active && !isSessionAuthenticationError(error)) {
                onFatalError();
            }
        });

        return () => {
            active = false;
        };

        /* */

        //  return allProjects;
    }, [context.currentIdentity, context.currentUserProjectsReload, loadBootstrapData, onFatalError]);

    // Handle project reload
    useEffect(() => {
        if (!loadBootstrapData) return;
        if (!context.currentUserProjectsReload) return;

        const tokenForProject = context.currentIdentity?.token ?? null;
        setCurrentUserProjectsLoaded(false, tokenForProject);
        setCurrentUserProjectsReload(false);
    }, [context.currentUserProjectsReload, loadBootstrapData]);

    // Scroll to top on route change
    useEffect(() => {
        document.querySelector('html').style.scrollBehavior = 'auto';
        window.scroll({top: 0});
        document.querySelector('html').style.scrollBehavior = '';
    }, [location.pathname]);

    // Set permissions
    useEffect(() => {
        if (!loadBootstrapData) return;
        let active = true;

        new SystemApi(getAuthorityConfiguration())
            .projectPermissions()
            .then(r => setPermissions(r.permissions))
            .catch(() => {
                if (active) onFatalError();
            });

        return () => {
            active = false;
        };
    }, [loadBootstrapData, onFatalError]);

    // Default subscription values
    useEffect(() => {
        if (!loadBootstrapData) return;
        let active = true;

        new SystemApi(getAuthorityConfiguration())
            .defaultSubscriptionValues()
            .then(r => {
                setDefaultSubscriptionValues(r);
                setDefaultSubscriptionValuesLoaded();
            })
            .catch(() => {
                if (active) onFatalError();
            });

        return () => {
            active = false;
        };
    }, [loadBootstrapData, onFatalError]);

    // Supported currencies
    useEffect(() => {
        if (!loadBootstrapData) return;
        let active = true;

        new SystemApi(getAuthorityConfiguration())
            .supportedCurrencies()
            .then(r => {
                setSupportedCurrencies(r);
                setSupportedCurrenciesLoaded();
            })
            .catch(() => {
                if (active) onFatalError();
            });

        return () => {
            active = false;
        };
    }, [loadBootstrapData, onFatalError]);

    // Redirect logic for onboarding if user is logged in
    useEffect(() => {
        // BrowserRouter updates history before its React transition commits. Do not
        // let an effect for the previous route overwrite a child's success navigation.
        if (location.pathname !== window.location.pathname) return;
        if (!context.currentIdentity) return;
        if (isSignOutPath(location.pathname)) return;
        if (shouldKeepInvitationRoute(location.pathname)) return;

        const isOnboardingRoute = location.pathname.startsWith('/onboarding');
        const hasPendingAddon = getLocalStoragePendingAddon() !== null;
        let addonFlowDismissed = isAddonFlowDismissed(context.currentIdentity.user.id);

        if (hasPendingAddon || context.currentUserProjects.length > 0) {
            clearAddonFlowDismissed();
            addonFlowDismissed = false;
        }

        if (
            location.pathname === '/onboarding/addon-project' &&
            !hasPendingAddon &&
            context.currentUserProjectsLoaded &&
            context.currentUserProjectsReload === false
        ) {
            navigate(context.currentUserProjects.length === 0 ? '/onboarding/project' : '/', {replace: true});
            return;
        }

        const projectOnboardingRedirect = getProjectOnboardingRedirect({
            pathname: location.pathname,
            currentIdentity: context.currentIdentity,
            currentUserProjectsLoaded: context.currentUserProjectsLoaded,
            currentUserProjectsReload: context.currentUserProjectsReload,
            currentUserProjectsCount: context.currentUserProjects.length,
            hasPendingAddon,
        });

        if (projectOnboardingRedirect !== null) {
            navigate(projectOnboardingRedirect, {replace: true});
            return;
        }

        if (
            hasPendingAddon &&
            context.currentUserProjectsLoaded &&
            context.currentUserProjects.length === 0 &&
            context.currentUserProjectsReload === false &&
            location.pathname === '/onboarding/project'
        ) {
            navigate('/onboarding/addon-project');
            return;
        }

        if (isOnboardingRoute) return;

        if (context.currentIdentity.user.newPasswordRequired) {
            navigate('/onboarding/password');
        } else if (
            context.currentIdentity.user.firstName === '' ||
            context.currentIdentity.user.lastName === '' ||
            context.currentIdentity.user.phone === ''
        ) {
            navigate('/onboarding/info');
        } else if (
            context.currentUserProjectsLoaded &&
            context.currentUserProjects.length === 0 &&
            context.currentUserProjectsReload === false
        ) {
            const zeroProjectRedirect = getZeroProjectOnboardingRedirect({
                pathname: location.pathname,
                hasPendingAddon,
                addonFlowDismissed,
            });

            if (zeroProjectRedirect !== null) {
                navigate(zeroProjectRedirect);
            }
        } else if (
            context.currentUserProjectsLoaded &&
            context.currentUserProjects.length > 0 &&
            hasPendingAddon
        ) {
            navigate('/onboarding/addon-project');
        }
    }, [
        context.currentIdentity,
        context.currentUserProjects.length,
        context.currentUserProjectsLoaded,
        context.currentUserProjectsReload,
        location.pathname,
        navigate,
    ]);

    // Rendering logic
    if (!context.localizationLoaded) {
        return (
            <div className={'h-lvh flex justify-center'}>
                <BigLoader/>
            </div>
        );
    }

    if (!context.currentIdentity) {
        // If user is not logged in, directly show the login page
        return <Routing/>; // Assumes Routing handles /sign/in
    }

    if (isSignOutPath(location.pathname)) {
        return <Routing/>;
    }

    if (
        context.currentUserProjectsLoaded &&
        context.permissionsLoaded &&
        context.defaultSubscriptionValuesLoaded &&
        context.supportedCurrenciesLoaded
    ) {
        if (routeProjectExists && routeProjectId !== null && routeProjectId !== context.currentProjectId) {
            return (
                <div className={'h-lvh flex justify-center'}>
                    <BigLoader/>
                </div>
            );
        }

        return <Routing key={context.currentProjectId}/>;
    }

    return (
        <div className={'h-lvh flex justify-center'}>
            <BigLoader/>
        </div>
    );
}

function locateTranslation(language, namespace) {
    const translations = {
        'cs': {'translations': translations_cs_CZ},
        'cs-CZ': {'translations': translations_cs_CZ},
    };
    return translations[language]?.[namespace];
}

function getAddonFromSearch(searchParams, rawSearch) {
    const addonType = searchParams.get('addon_type');
    const addonToken = searchParams.get('addon_token');
    if (addonType && addonToken) {
        return {type: addonType, token: addonToken, lang: searchParams.get('lang') ?? undefined};
    }

    const queryToken = searchParams.get('upgates_token');

    if (queryToken !== null) {
        return {type: 'upgates', token: queryToken, lang: searchParams.get('lang') ?? undefined};
    }

    const malformedTokenMatch = rawSearch.match(/[?&]upgates_token([^=&]+)/);

    if (!malformedTokenMatch) {
        return null;
    }

    return {type: 'upgates', token: decodeURIComponent(malformedTokenMatch[1]), lang: searchParams.get('lang') ?? undefined};
}

export default DatixoApplication;
